const registry = require('../registry');

/**
 * @type {import('../registry').ExtractAccessFunction}
 */
function extractAccess(hNode, options) {
    // navHeadings are all Navigation Menu entries.
    // So we (almost) always need to generate page Specs/access for them.
    // The one exception is if it is hidden for both ADMIN and USERS
    const { roles } = options;
    const { id, title, hiddenFor, hideOn, customizableVisibility, children: _children } = hNode;
    const pageId = customizableVisibility?.[0]?.accessTargetId || id;

    if (hiddenFor?.includes('ADMIN') && hiddenFor?.includes('USER')) {
        return { spec: [], access: {} };
    }

    // We have a something we need specs and access for:
    // start with the assumption it is a menu
    let type = getType(hNode);
    let children = _children || [];
    if (type === 'pageList' || children[0]?.hNodeType == 'TabNav') {
        children = children[0].children;
    }

    let nodeSpec = {
        title,
        pageId,
        type,
        tabs: [],
        actions: []
    };

    let nodeAccess = roles.reduce((acc, role) => {
        acc[role] = acc[role] || {};
        acc[role][pageId] = {
            // including title has no other use than that it is really helpful for debugging
            // as otherwise it is near impossible  for a human to figure out what the access is for.
            title,
            // We should not include `&& !hideOn?.includes('NATIVE')` here,
            // as "mobile" is for both native, and mobile-web
            mobile: hasAccess(hNode, role, 'MOBILE'),
            // But for desktop, if it is not allowed to be shown on the WEB, then there is no way to show it on desktop:
            desktop: hasAccess(hNode, role, 'DESKTOP') && !hideOn?.includes('WEB')
        };
        return acc;
    }, {});

    if (type === 'menu') {
        // children are menu's or pages themselves, and go at the same level as this one:
        const [allSpecs, allAccess] = children.reduce(
            ([specs, access], child) => {
                const extractChildRoles =
                    registry[`${child.hNodeTypeGroup}s`]?.[child.hNodeType]?.extractAccess ??
                    registry.hNode.extractAccess;

                const { spec: childSpecs, access: childAccess } = extractChildRoles(child, options);
                specs = specs.concat(childSpecs);
                access = roles.reduce((acc, role) => {
                    acc[role] = acc[role] || {};
                    acc[role] = { ...acc[role], ...childAccess[role] };
                    return acc;
                }, access);

                return [specs, access];
            },
            [[nodeSpec], nodeAccess]
        );
        return { spec: allSpecs, access: allAccess };
    } else {
        // pages, (including tabs and page lists) need to nest the children's specs and access
        [nodeSpec, nodeAccess] = children.reduce(
            ([spec, access], child) => {
                const extractChildRoles =
                    registry[`${child.hNodeTypeGroup}s`]?.[child.hNodeType]?.extractAccess ??
                    registry.hNode.extractAccess;

                const { spec: childSpecs, access: childAccess } = extractChildRoles(child, options);

                //different from menu, child specs are nested in the page's tabs or actions as applicable
                // eslint-disable-next-line no-unused-vars
                spec.tabs = spec.tabs.concat(childSpecs.filter(cs => cs.isTab).map(({ isTab, ...cs }) => cs));
                // eslint-disable-next-line no-unused-vars
                spec.actions = spec.actions.concat(childSpecs.filter(cs => !cs.isTab).map(({ isTab, ...cs }) => cs));

                //different from menu. child access is embedded in the page access
                access = roles.reduce((acc, role) => {
                    acc[role] = acc[role] || {};
                    acc[role][pageId] = { ...acc[role][pageId], ...childAccess[role] };
                    return acc;
                }, access);

                return [spec, access];
            },
            [nodeSpec, nodeAccess]
        );

        return { spec: [nodeSpec], access: nodeAccess };
    }
}

function redact(hNode, access = {}, defaultAccess) {
    const { id, customizableVisibility = [], hiddenFor, showForViewPort = [], hideForViewport = [], children } = hNode;

    const accessTargetId = customizableVisibility[0]?.accessTargetId ?? '';
    // Navigation is different from components.
    // If there is no limitation defined on the access, it depends on default access if it is shown or not.
    const limitation = access[id] || access[accessTargetId] || defaultAccess;

    let viewportRestrictions = {
        showForViewPort,
        hideForViewport
    };

    if (limitation != null) {
        if (typeof limitation === 'boolean') {
            if (!limitation) {
                return undefined;
            } else {
                // if default access allows access to an un configured menu item
                // it still could be the user has no access to it, if it is hidden for both ADMIN and USER
                if (hiddenFor?.includes('ADMIN') && hiddenFor?.includes('USER')) {
                    return undefined;
                }
            }
        } else {
            // if BOTH mobile and desktop are not allowed, redact this node.
            if (!limitation.mobile && !limitation.desktop) {
                return undefined;
            } else if (!limitation.mobile) {
                // only mobile is not allowed, add a view port limitation for mobile
                viewportRestrictions.hideForViewport.push('MOBILE');
            } else if (!limitation.desktop) {
                // only desktop is not allowed, add a view port limitation for desktop
                viewportRestrictions.hideForViewport.push('DESKTOP');
            }
        }
    }

    // If this is some sort of page, the access for the children is defined within the context of this page.
    const type = getType(hNode);
    const subAccess = type === 'menu' ? access : access[id] || access[accessTargetId] || {};

    return {
        ...hNode,
        ...viewportRestrictions,
        children: children
            ?.map(child => {
                const redactChild =
                    registry[`${child.hNodeTypeGroup}s`]?.[child.hNodeType]?.redact ?? registry.hNode.redact;

                //Special for pages and pageList, their children's access is defined within the context of their own access
                return redactChild(child, subAccess, defaultAccess);
            })
            .filter(x => !!x)
    };
}

function getType(hNode) {
    let type = 'menu';
    if (hNode.hNodeType === 'TabNavHeading') {
        // although, if this is a TabNavHeading, it is a tab
        type = 'tab';
    } else if (
        hNode.hNodeType == 'Page' ||
        !['page', 'navHeading', 'nav'].includes(hNode.children?.[0]?.hNodeTypeGroup)
    ) {
        // otherwise if the first child is not a navHeading, it is a page
        type = 'page';
    } else if (['NavigationSelectedContextPage', 'NavHeadingRecordList'].includes(hNode.children?.[0]?.hNodeType)) {
        // if the first child is a navHeading, but it is also a NavHeadingRecordList, it is a pageList
        type = 'pageList';
    }
    return type;
}

/**
 *
 * @param {import('@sstdev/lib_metadata-transforms/test/testSetup/embedInSubMenu').AccessModifiers} hNode
 * @param {'USER' | 'ADMIN' | 'SUPPORT'} role
 * @param {'MOBILE' | 'TABLET' | 'DESKTOP'} viewport
 * @returns
 */
function hasAccess(hNode, role, viewport) {
    //if this role has no access, it has no access
    if (hNode.hiddenFor?.includes(role)) return false;
    // if the role DOES (potentially) has access:
    // if the viewport is explicitly allowed: it has access
    if (hNode.showForViewPort?.includes(viewport)) return true;
    //if the viewport is explicitly allowed for some other viewport, but not this one: it does not have access
    if (hNode.showForViewPort?.length && !hNode.showForViewPort.includes(viewport)) return false;
    // if the viewport is explicitly disallowed: it does not have access
    if (hNode.hideForViewport?.includes(viewport)) return false;
    // if the viewport is explicitly disallowed for some other viewport but not this one, it has access
    if (hNode.hideForViewport?.length && !hNode.hideForViewport.includes(viewport)) return true;
    // if there are no explicit rules, it has access
    return true;
}

function unexpected() {
    throw new Error('Unexpected to process a NavHeadingRecordList. It should have been skipped.');
}

const navHeadings = {
    NavHeading: { extractAccess, redact },
    TabNavHeading: { extractAccess, redact },
    //this one shouldn't occur, but just in case:
    NavHeadingRecordList: {
        extractAccess: unexpected
    }
};

registry.register('navHeadings', navHeadings);
module.exports = navHeadings;
